/**
 * API `work_pod_physical` — refonte variantes matrix Option M (Phase 6-FE).
 *
 * 6 endpoints REST documentés dans
 * `backend/app/api/work_pod_physical.py` :
 *
 *   GET    /api/works/{work_id}/pod-physical               → matrice active (404 si absente)
 *   POST   /api/works/{work_id}/pod-physical               → crée la matrice (201, 409 si déjà active, 422 caps)
 *   PATCH  /api/work-pod-physical/{id}                     → PATCH partiel
 *   POST   /api/work-pod-physical/{id}/archive             → archive (204)
 *   POST   /api/work-pod-physical/{id}/cells/override      → set/unset override
 *   GET    /api/work-pod-physical/{id}/preview             → preview cellules + pricing
 */

import { apiClient } from './client'
import type {
  CellOverridePayload,
  MatrixPreviewBundle,
  WorkPodPhysicalCreateRequest,
  WorkPodPhysicalResponse,
  WorkPodPhysicalUpdateRequest,
} from '@/types/work-pod-physical'

export interface PublishMatrixRequest {
  draft_id: number
  mockup_template_ids: number[]
}

export interface PublishMatrixResponse {
  queue_item_id: number
  platform: string
  draft_id: number
  work_id: number
  pod_physical_id: number
  work_variant_id: number
  mockup_template_ids: number[]
  mockup_ids: number[]
  status: string
}

export const workPodPhysicalApi = {
  /** Retourne `null` si l'œuvre n'a aucune matrice active (404 → null). */
  getForWork: async (workId: number): Promise<WorkPodPhysicalResponse | null> => {
    try {
      const { data } = await apiClient.get<WorkPodPhysicalResponse>(`/works/${workId}/pod-physical`)
      return data
    } catch (err) {
      const status = (err as { status?: number }).status
      if (status === 404) return null
      throw err
    }
  },

  create: async (
    workId: number,
    payload: WorkPodPhysicalCreateRequest,
  ): Promise<WorkPodPhysicalResponse> => {
    const { data } = await apiClient.post<WorkPodPhysicalResponse>(
      `/works/${workId}/pod-physical`,
      payload,
    )
    return data
  },

  update: async (
    podPhysicalId: number,
    payload: WorkPodPhysicalUpdateRequest,
  ): Promise<WorkPodPhysicalResponse> => {
    const { data } = await apiClient.patch<WorkPodPhysicalResponse>(
      `/work-pod-physical/${podPhysicalId}`,
      payload,
    )
    return data
  },

  archive: async (podPhysicalId: number): Promise<void> => {
    await apiClient.post(`/work-pod-physical/${podPhysicalId}/archive`)
  },

  setCellOverride: async (
    podPhysicalId: number,
    payload: CellOverridePayload,
  ): Promise<WorkPodPhysicalResponse> => {
    const { data } = await apiClient.post<WorkPodPhysicalResponse>(
      `/work-pod-physical/${podPhysicalId}/cells/override`,
      payload,
    )
    return data
  },

  preview: async (podPhysicalId: number): Promise<MatrixPreviewBundle> => {
    const { data } = await apiClient.get<MatrixPreviewBundle>(
      `/work-pod-physical/${podPhysicalId}/preview`,
    )
    return data
  },

  publish: async (
    podPhysicalId: number,
    payload: PublishMatrixRequest,
  ): Promise<PublishMatrixResponse> => {
    const { data } = await apiClient.post<PublishMatrixResponse>(
      `/work-pod-physical/${podPhysicalId}/publish`,
      payload,
    )
    return data
  },
}
